import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

const SITE_URL = 'https://cuj-csi.web.app';
const DEFAULT_IMAGE = `${SITE_URL}/csi_logo.png`;

const ROUTE_SEO = [
  {
    pattern: '/membership/:year',
    title: 'CSI CUJ Membership | Computer Society of India at CUJ',
    description:
      'Membership details for CSI CUJ, the Computer Society of India student branch in the Department of Computer Science and Engineering (CSE), Central University of Jharkhand (CUJ).',
  },
  {
    pattern: '/student-council/:year',
    title: 'CSI CUJ Student Council | CSE Department, CUJ',
    description:
      'Meet the CSI CUJ student council from CSE, Central University of Jharkhand. Explore leadership roles, branch activities, and student initiatives.',
  },
  {
    pattern: '/events/:year',
    title: 'CSI CUJ Events | CSE, CUJ, Computer Society of India',
    description:
      'Explore events, workshops, and technical activities by CSI CUJ in the Department of Computer Science and Engineering at Central University of Jharkhand.',
  },
  {
    pattern: '/contact-us',
    title: 'Contact CSI CUJ | CSE Department, Central University of Jharkhand',
    description:
      'Contact the CSI Student Branch of Central University of Jharkhand (CUJ), Department of Computer Science and Engineering (CSE), Ranchi.',
  },
  {
    pattern: '/gallery',
    title: 'CSI CUJ Gallery | CSE Activities at Central University of Jharkhand',
    description:
      'Photo gallery of CSI CUJ events, workshops, and student activities at the CSE Department, Central University of Jharkhand.',
  },
  {
    pattern: '/',
    title: 'CSI CUJ | Computer Society of India Student Branch at CSE, CUJ',
    description:
      'Official CSI CUJ website for the Computer Society of India student branch at the Department of Computer Science and Engineering (CSE), Central University of Jharkhand (CUJ), Ranchi.',
  },
];

const KEYWORDS = [
  'CSI CUJ',
  'CSI',
  'CUJ',
  'CSE CUJ',
  'Computer Society of India',
  'Central University of Jharkhand',
  'Computer Science and Engineering',
  'CSI student branch',
  'CSI Central University of Jharkhand',
  'CSE Ranchi',
];

function setHeadTag(kind, key, content) {
  let element = document.head.querySelector(`meta[${kind}="${key}"]`);
  if (kind === 'rel') {
    element = document.head.querySelector(`link[rel="${key}"]`);
  }

  if (!element) {
    if (kind === 'rel') {
      element = document.createElement('link');
      element.setAttribute('rel', key);
    } else {
      element = document.createElement('meta');
      element.setAttribute(kind, key);
    }
    document.head.appendChild(element);
  }

  if (kind === 'rel') {
    element.setAttribute('href', content);
    return;
  }

  element.setAttribute('content', content);
}

function resolveSeo(pathname) {
  return ROUTE_SEO.find((item) => matchPath(item.pattern, pathname)) || ROUTE_SEO[ROUTE_SEO.length - 1];
}

const SeoManager = () => {
  const location = useLocation();

  useEffect(() => {
    const { title, description } = resolveSeo(location.pathname);
    const canonicalUrl = `${SITE_URL}${location.pathname}`;

    document.title = title;
    setHeadTag('name', 'description', description);
    setHeadTag('name', 'keywords', KEYWORDS.join(', '));
    setHeadTag('name', 'robots', 'index, follow');
    setHeadTag('property', 'og:type', 'website');
    setHeadTag('property', 'og:site_name', 'CSI CUJ');
    setHeadTag('property', 'og:title', title);
    setHeadTag('property', 'og:description', description);
    setHeadTag('property', 'og:url', canonicalUrl);
    setHeadTag('property', 'og:image', DEFAULT_IMAGE);
    setHeadTag('name', 'twitter:card', 'summary_large_image');
    setHeadTag('name', 'twitter:title', title);
    setHeadTag('name', 'twitter:description', description);
    setHeadTag('name', 'twitter:image', DEFAULT_IMAGE);
    setHeadTag('rel', 'canonical', canonicalUrl);

    let jsonLdTag = document.getElementById('csi-cuj-structured-data');
    if (!jsonLdTag) {
      jsonLdTag = document.createElement('script');
      jsonLdTag.setAttribute('id', 'csi-cuj-structured-data');
      jsonLdTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(jsonLdTag);
    }

    jsonLdTag.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      name: 'CSI Student Branch, Central University of Jharkhand',
      alternateName: ['CSI CUJ', 'CSI CSE CUJ'],
      url: canonicalUrl,
      parentOrganization: {
        '@type': 'Organization',
        name: 'Computer Society of India',
      },
      department: {
        '@type': 'CollegeOrUniversity',
        name: 'Department of Computer Science and Engineering, Central University of Jharkhand',
      },
      sameAs: [SITE_URL],
    });
  }, [location.pathname]);

  return null;
};

export default SeoManager;
